import * as React from 'react';
import { Alert, Button, Col, Layout, List, Modal, Row, Spin, Card, Input, Form, Result, Image, Space, Avatar, Divider, message } from 'antd';
import LoginForm from "./LoginForm"
import { AuthContext } from "../../utilites/auth";
import { Link, useHistory, useLocation } from 'react-router-dom';
import { AuthApi } from '../../apis/auth-api';
import { RightApi } from '../../apis/right-api';
import { CompanyApi } from '../../apis/company-api';
import { UserPreferenceApi } from '../../apis/user-preference-api';
import { LoginCookie } from '../../models/login-cookie-interface';
import { useRef, useState } from 'react';
import { UserProfile } from '../../models/user-profile-inteface';
import { useEffect } from 'react';
import GovendaLogo from '../../assets/govendaAdminLogo';
import GovendaCSLogo from '../../assets/GovendaCSLogo.png';
import 'react-perfect-scrollbar/dist/css/styles.css';
import PerfectScrollbar from 'react-perfect-scrollbar'
import { useCookies } from 'react-cookie';
import { AxiosResponse } from 'axios';
import TwoFAModal from './modals/TwoFAModal';
import Register2FAModal from './modals/Register2FAModal';
import { CSPORTAL_APPLICATION_ID, CSPORTAL_APPLICATION_STRING } from '../../utilites/api';
import { UserApi } from '../../apis/user-api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronCircleLeft,
    faChevronDoubleRight
} from '@fortawesome/pro-solid-svg-icons';
import BgAnimationCanvas from './BackgroundAnimation';
import QueueAnim from 'rc-queue-anim';
import { ProfileCommonCompany } from '../../models/profile-common-company-interface';
import { UserPreference } from '../../models/user-preference-interface';
import { ThemeNameToIdHelper } from '../../utilites/themes/ThemeSelector';
import FooterLinks from '../../components/FooterLinks';
import { Logger } from '../../utilites/loglySetup';
import { number } from 'yup';
import { FormikProps } from 'formik';

interface stateType {
    referer: { pathname: string, search: string }
}

interface LoginFormFields {
    UserName: string;
    Password: string;
    AuthenticationCode: string;
    RememberMe: boolean;
    TrustThisDevice: boolean;
}

interface GlobalUserProps {
    userId: number;
    fullName: string;
    initials: string;
    userName: string;
    companies: ProfileCommonCompany[];
    sessionTimeout: number;
}

const LoginPage = () => {
    const { isLoggedIn, setLoggedIn, setProfile, profile } = React.useContext(AuthContext);
    const history = useHistory();
    const formRef = React.useRef<FormikProps<any>>();
    const { state } = useLocation<stateType>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [loginProfile, setLoginProfile] = useState<UserProfile>();
    const [key, setKey] = useState<string>('login');
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [is2FAModalVisible, setIs2FAModalVisible] = useState<boolean>(false);
    const [is2FAModalLoading, setIs2FAModalLoading] = useState<boolean>(false);
    const [isRegister2FAModalVisible, setIsRegister2FAModalVisible] = useState<boolean>(false);
    const [isRegister2FAModalLoading, setIsRegister2FAModalLoading] = useState<boolean>(false);
    const [resetPasswordLoading, setResetPasswordLoading] = useState<boolean>(false);
    const [emailReset, setEmailReset] = useState<string>('');
    const [companyLogos, setCompanyLogos] = useState<{id: number, base64Logo: string}[]>([]);
    const [twoFAProps, setTwoFAProps] = useState<{maskedNumber: string, daysToRememberDevice: string}>();
    const [loginFields, setLoginFields] = useState<LoginFormFields>();
    const [isAutoFocus, setIsAutoFocus] = useState<boolean>(false);
    const [form] = Form.useForm();
    const [cookies, setCookie, removeCookie] = useCookies();
    
    const rememberMe = useRef();

    useEffect(() => {
        document.title = `Govenda CS Portal Sign In`;
        setIsLoading(true);
        const checkSession = async () => {
            if (cookies.sessionUserName !== undefined) {
                try {                 
                    const userProfile = await AuthApi.getUserProfile();
                    setLoginProfile(userProfile);
                    processLogin(userProfile.userName, userProfile.companies, userProfile.userId);
                    setIsLoading(false);
                } catch (e) {
                    Logger().error(e);
                    setIsLoading(false);
                }
            }
        }
        checkSession();

        showSamlErrorModalIfNecessary();
        setIsLoading(false);
        message.destroy('errorMsg');
    }, [])

    /**
     * Searches the current query string for indications that any SAML/SSO errors occurred and need to be displayed to the user
     * @returns void
     */
    const showSamlErrorModalIfNecessary = (): void => {
        if (window.location.search.indexOf('saml_not_configured') > -1) {
            let newSearch = window.location.search.replace('saml_not_configured=true', '').replace('&&', '&');
            if (newSearch == '?') { newSearch = ''; }
            window.history.pushState(null, '', window.location.href.substring(0, window.location.href.indexOf('?')) + newSearch);
            Modal.error({
                title: 'SSO not configured',
                content: 'Single sign-on is not configured for your Govenda account.',
                centered: true,
                okText: 'Continue'
            });
        }
        else if (window.location.search.indexOf('saml_not_found') > -1) {
            let newSearch = window.location.search.replace('saml_not_found=true', '').replace('&&', '&');
            if (newSearch == '?') { newSearch = ''; }
            window.history.pushState(null, '', window.location.href.substring(0, window.location.href.indexOf('?')) + newSearch);
            Modal.error({
                title: 'Account not found',
                content: 'We could not find a valid Govenda account for you. Please email support@govenda.com if you have received this message in error.',
                centered: true,
                okText: 'Continue'
            });
        }
    };

    /**
     * Login submission. Handling of various login response scenarios are handled here. 
     * On successful login, either show choose company view if user has multiple companies or
     * redirect to dashboard if user has only one company
     * @param data login form fields
     * @returns void
     */
    async function handleSubmit(data: LoginFormFields) {
        setIsLoading(true);

        // get cookie for device token here
        const deviceToken = localStorage.getItem(`device_token_csportal_cookie_${data.UserName.toLowerCase()}`);

        data.UserName = data.UserName.trim();
        const loginCookie: LoginCookie = {
            username: data.UserName,
            password: data.Password,
            authenticationCode: data.AuthenticationCode,
            deviceToken: deviceToken,
            applicationId: CSPORTAL_APPLICATION_ID
        }

        try {
            if(formRef.current?.values.RememberMe !== undefined)
                rememberMe.current = formRef.current?.values.RememberMe;

            const loginResponse = await AuthApi.postLogin(loginCookie);

            // handle login fail
            if (loginResponse !== undefined && loginResponse.status === 400) {
                setLoginFields(prev => ({...prev, UserName: data.UserName, Password: data.Password, RememberMe: data.RememberMe}));
                handleLoginError(loginResponse, data.UserName); 
                return;
            }

            // handle remember me cookie
            if (data.RememberMe || (data.RememberMe === undefined && rememberMe.current))
                setCookie('username', data.UserName);
            else
                removeCookie('username', { path: "/" });


            // handle trust device cookie if device token not found
            if (!!!deviceToken) {
                if (data.TrustThisDevice) {
                    try {
                        const deviceToken = await AuthApi.postSaveAuthenticationDevice();
                        localStorage.setItem(`device_token_csportal_cookie_${data.UserName.toLowerCase()}`, deviceToken);
                    } catch (e) {
                        Logger().error(e);
                    }                
                } else {
                    localStorage.removeItem(`device_token_csportal_cookie_${data.UserName.toLowerCase()}`);
                }                
            }

            setIs2FAModalVisible(false);

            const userProfile = await AuthApi.getUserProfile();
            
            setLoginProfile(userProfile);


            
            processLogin(userProfile.userName, userProfile.companies, userProfile.userId);
            setIsLoading(false);
            window.location.assign('/admin/dashboard');
        } catch (e) {
            Logger().error(e);
            loginError();
            setIsLoading(false);
        }
    }

    /**
     * Handle login error after handleSubmit called.
     * @param loginResponse response from server
     * @param username
     */
    const handleLoginError = async (loginResponse: AxiosResponse, username: string) => {
        switch (loginResponse.data.error) {
            case "register_2fa":
                localStorage.removeItem(`device_token_csportal_cookie_${username.toLowerCase()}`);
                setTwoFAProps((prev) => ({...prev, daysToRememberDevice: loginResponse.data.error_description.split('=')[1]}));
                setIsRegister2FAModalVisible(true);
                break;
            case "needs_2fa":
                const pairs = loginResponse.data.error_description.split("&");
                const twoFAObject = {
                    maskedNumber: pairs[0].split('=')[1],
                    daysToRememberDevice: pairs[1].split('=')[1]
                };
                localStorage.removeItem(`device_token_csportal_cookie_${username.toLowerCase()}`);
                setTwoFAProps(twoFAObject);
                setIs2FAModalVisible(true);
                break;
            case "invalid_code":
                Modal.error({
                    title: 'Login Failed',
                    content: 'Invalid 6 digit code provided',
                    centered: true
                });
                setIs2FAModalLoading(false);
                break;
            case "resent_code":
                Modal.error({
                    title: 'Login Failed',
                    content: 'A new authentication code has been sent to the provided number due to multiple failed attempts.',
                    centered: true
                });
                setIs2FAModalLoading(false);
                break;
            case "saml_only":
                Modal.error({
                    title: 'Invalid Login',
                    content: 'You recently requested a password reset for your Govenda account. However, your company uses a single-sign-on provider. Please login using that provider. If you have any issues, please contact your company administrator or email support@govenda.com. Thank you.',
                    centered: true
                });
                break;
            case "invalid_grant":
                Modal.error({
                    title: 'Unexpected Error',
                    content: (<><p>The email address or password that you entered didn't match our records.</p><p>Please try again or click "Forgot?" to request a password reset.</p></>),
                    centered: true,
                    okText: 'Try Again'
                });
                break;
            case "approaching_attempt_limit":
                Modal.error({
                    title: 'Unexpected Error',
                    content: <p className="error-description">{loginResponse.data.error_description}</p>,
                    centered: true,
                    okText: 'Try Again'
                });
                break;
            case "account_locked":
                Modal.error({
                    title: 'Account Locked',
                    content: <p className="error-description">{loginResponse.data.error_description}</p>,
                    centered: true,
                    okText: 'Try Again Later'
                });
                break;
            default:
                const hasLoggedIn = await AuthApi.getHasLoggedIn(username);
                if (!hasLoggedIn) {
                    Modal.error({
                        title: 'Login Failed',
                        content: 'Since this is the first time you are logging in to the Board Portal, you will need to obtain your login credentials. In case you have misplaced them, we have just resent the email to set up your account. Please follow the instructions in the email, and you\'ll be logged in in no time.',
                        centered: true
                    });
                } else  
                    loginError();
                break;
        }
        setIsLoading(false);
    }

    /**
     * Show forgot password modal
     */
    const handleForgot = () => {
        setIsModalVisible(true);
    }

    /**
     * Show generic login error modal
     */
    const loginError = () => {
        Modal.error({
            title: 'Unexpected Error',
            content: (<><p>An unexpected error has occurred.</p><p>Please try again.</p></>),
            centered: true,
            okText: 'Try Again'
        });
    }

    /**
     * Set user rights and profile, and redirects to company dashboard
     * @param companyId 
     * @param userEmail 
     */
    const processLogin = async (userEmail: string, userCompanies: ProfileCommonCompany[], userId: number = 0) => {
        let companyUserProfile = {
            fullName: '',
            firstName: '',
            lastName: '',
            sessionTimeout: [],
            sessionTimeoutId: 0,
        };

        // Need to add logic to get theme/user setting from service 
        /*let themePreference = await UserPreferenceApi.getCurrentUserPreferenceValueForApplication(CSPORTAL_APPLICATION_STRING, 'theme');

        if(themePreference === null) {
            const data: UserPreference = {
                value: 'light',
                application: CSPORTAL_APPLICATION_STRING,
                key: 'theme',
            };

            try {
                await UserPreferenceApi.patchSingleUserPreference(data);
                setCookie('themeSetting', 1, {path: "/"});
            }
            catch (e) {
                Logger().error(e);
            }
        } else {
            setCookie('themeSetting', ThemeNameToIdHelper(themePreference), {path: "/"});
        }*/

        //Redirect
        if (state && state.referer && state.referer.pathname.includes(`/admin`)) {
          //window.location.assign(state.referer.pathname + state.referer.search ?? "");
          removeCookie('forcedSignOut', {path: "/"});
        } else {
          history.push('admin/dashboard');
        }
    }

    /**
     * Cancel choosing company and navigate back to login form
     */
    const handleCancel = () => {
        setKey('login');
        // disabled and delay focus for Safari
        setTimeout(() => {
            setIsLoading(false);
            setIsAutoFocus(true);
        }, 1000);
    }

    const contentList = {
        login: <Spin spinning={isLoading}>
                    <Row className="login-header" justify="space-between" align="middle">
                        <Image src={GovendaCSLogo} preview={false} />
                    </Row>
                    <LoginForm formRef={formRef} handleForgot={handleForgot} onSubmit={handleSubmit} isInputDisabled={isLoading} isAutoFocus={isAutoFocus}/>
                </Spin>,
    };

    /**
     * Initial reset password form submission used for validation
     */
    const handleOk = async () => {
        try {
            await form.validateFields();
            setResetPasswordLoading(true);
        } catch (errorInfo) {
            Logger().error("ErrorInfo: ", errorInfo);
        }
    }
    
    /**
     * Reset password form submission
     * @param values email
     */
    const onFinish = async (values: {email: string}) => {
        try {
            const response = await AuthApi.getGenerateResetToken(values.email);
            setResetPasswordLoading(false);
            setEmailReset(values.email);
        } catch (e) {
            Logger().error(e);
        }
    };

    /**
     * Dismiss reset password form
     */
    const handleDismiss = () => {
        form.resetFields();
        setEmailReset('');
        setIsModalVisible(false);
    }

    return (
        <Layout className="login-layout">
            <BgAnimationCanvas id="BgAnimationCanvas" />
            <Card
                id="loginCard"
                activeTabKey={key}
                className={key}
            >
                {contentList[key]}
                <FooterLinks />
            </Card> 
            <Modal 
                className="reset-password-modal"
                title="Reset Password" 
                visible={isModalVisible} 
                onOk={handleOk} 
                onCancel={handleDismiss}
                getContainer={false}
                footer={emailReset.length > 0 ? null : [
                    <Button id="CancelResetPasswordButton" key="back" onClick={handleDismiss}>
                    Cancel
                    </Button>,
                    <Button id="SubmitResetPasswordButton" key="submit" type="primary" form="resetPasswordForm" htmlType="submit" loading={resetPasswordLoading} onClick={handleOk}>
                    Send Email
                    </Button>,
                ]}
                centered
            >
                {
                    emailReset.length === 0
                    ? 
                    <>
                    <p>Provide us with your email address, and we will send you an email with instructions on how to reset your password.</p>
                    <p>You will be signed in again in no time!</p>
                    <Form
                        form={form}
                        id="resetPasswordForm"
                        onFinish={onFinish}
                        initialValues={{email: formRef?.current?.values?.UserName}}
                    >
                        <Form.Item
                            name="email"
                            rules={[{ required: true, message: 'Please input your email address.' }]}
                        >
                            <Input size="large" placeholder="Email Address" />
                        </Form.Item>
                    </Form>
                    </>
                    :
                    <Result
                        status="success"
                        subTitle={`An email has been sent to ${emailReset} with information to reset your password.`}
                        extra={[
                        <Button id="DismissResetPasswordSuccessButton" key="dismiss" onClick={handleDismiss}>
                            Dismiss
                        </Button>,
                        ]}
                    />
                }
            </Modal>
            <TwoFAModal 
                twoFAProps={twoFAProps} 
                isModalVisible={is2FAModalVisible} 
                setIsModalVisible={setIs2FAModalVisible}
                handleSubmit={handleSubmit}
                loginFields={loginFields}
                setIsMainLoading={setIsLoading}    
                is2FAModalLoading={is2FAModalLoading}  
                setIs2FAModalLoading={setIs2FAModalLoading}
            />
            <Register2FAModal
                setTwoFAProps={setTwoFAProps}
                isModalVisible={isRegister2FAModalVisible}
                setIsModalVisible={setIsRegister2FAModalVisible}
                isLoading={isRegister2FAModalLoading}
                setIsLoading={setIsRegister2FAModalLoading}
                loginFields={loginFields}
                setIs2FAModalVisible={setIs2FAModalVisible}
                profile={profile}
            />
        </Layout>
    );
}

export default LoginPage;
